import { z } from 'zod';

// Schema para producto seleccionado en la UI
export const selectedProductSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  description: z.string().optional(),
  sku: z.string().optional(),
  image: z.string().optional(),
  price: z.number().optional(),
  stock: z.number().optional(),
  quantity: z.number().min(1, 'La cantidad debe ser al menos 1'),
});

// Schema para línea de entrada (estructura de la API)
export const entryLineSchema = z.object({
  product_id: z.number(),
  qty_expected: z.number().min(1, 'La cantidad esperada debe ser al menos 1'),
});

// Schema de validación para crear entrada (formulario)
export const createEntrySchema = z.object({
  estimated_delivery_at: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true; // Opcional
        // Aceptar formato "YYYY-MM-DD HH:mm:ss" o "YYYY-MM-DD"
        const dateStr = val.includes(' ') ? val.replace(' ', 'T') : val;
        const date = new Date(dateStr);
        return !isNaN(date.getTime());
      },
      { message: 'La fecha debe ser válida' }
    ),

  boxes_count: z
    .number()
    .int('El número de cajas debe ser un entero')
    .min(1, 'El número de cajas debe ser al menos 1')
    .optional()
    .nullable(),

  tracking_code: z
    .string()
    .max(255, 'El código de tracking no puede exceder 255 caracteres')
    .optional()
    .nullable(),

  products: z
    .array(selectedProductSchema)
    .min(1, 'Debe seleccionar al menos un producto')
    .refine(
      (products) => {
        // Validar que no haya productos duplicados
        const productIds = products.map((p) => Number(p.id));
        return new Set(productIds).size === productIds.length;
      },
      { message: 'No se permiten productos duplicados' }
    ),
});

// Schema para enviar a la API (estructura final)
export const createEntryAPISchema = z.object({
  estimated_delivery_at: z.string().nullable().optional(),
  boxes_count: z.number().int().min(1).nullable().optional(),
  tracking_code: z.string().max(255).nullable().optional(),
  metadata: z.record(z.any()).nullable().optional(),
  lines: z
    .array(entryLineSchema)
    .min(1, 'Debe incluir al menos una línea de producto'),
});

// Tipos TypeScript derivados de los schemas
export type SelectedProductData = z.infer<typeof selectedProductSchema>;
export type EntryLine = z.infer<typeof entryLineSchema>;
export type CreateEntryFormData = z.infer<typeof createEntrySchema>;
export type CreateEntryAPIData = z.infer<typeof createEntryAPISchema>;

// Función para transformar datos del formulario a formato de API
export const transformFormDataToAPI = (
  formData: CreateEntryFormData
): CreateEntryAPIData => {
  // Formatear fecha si existe (debe estar en formato "YYYY-MM-DD HH:mm:ss")
  let formattedDate: string | null = null;
  if (formData.estimated_delivery_at && formData.estimated_delivery_at.trim() !== '') {
    formattedDate = formData.estimated_delivery_at;
  }

  return {
    estimated_delivery_at: formattedDate,
    boxes_count: formData.boxes_count || null,
    tracking_code: formData.tracking_code || null,
    metadata: null, // Por ahora null, se puede extender después
    lines: formData.products.map((product) => ({
      product_id: Number(product.id),
      qty_expected: product.quantity,
    })),
  };
};

// Schema para actualización de entrada (reutiliza la misma estructura que creación)
export const updateEntryAPISchema = createEntryAPISchema;

// Tipo para actualización de entrada
export type UpdateEntryAPIData = CreateEntryAPIData;

// Función para transformar datos del formulario a formato de actualización API
export const transformFormDataToUpdateAPI = (
  formData: CreateEntryFormData
): UpdateEntryAPIData => {
  // Reutilizamos la misma lógica de transformación que para creación
  return transformFormDataToAPI(formData);
};

